// Formatação dos relatórios para WhatsApp (VD) — pura.
//
// ★ ESTE MÓDULO NÃO CALCULA NADA. Ele recebe números que já existem (de
// `consolidarPeriodo`/`consolidarMes` e da folha) e devolve texto. Se algum dia
// alguém se pegar somando aqui, é sinal de fonte errada.
//
// Regras do dono, literais:
//   - denominador zero → "—", NUNCA "0%": "não dá para saber" não é "zero";
//   - sem tabela (WhatsApp não tem), linhas curtas, emoji só no cabeçalho;
//   - nenhum nome de paciente, em lugar nenhum.

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "R$ 4.500,00" — com espaço comum, não o NBSP que o Intl emite. */
export function moeda(valor) {
  const n = Number(valor) || 0;
  return n
    .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
    .replace(/ /g, ' ');
}

/**
 * "43%" — ou "—" quando o denominador é zero.
 *
 * ★ A regra que cada percentual do relatório obedece. `pct()` de metas/calculo
 * devolve 0 para denominador zero porque a tela de Metas desenha barras; aqui o
 * texto tem de dizer que não há o que medir.
 */
export function porcento(parte, total) {
  const t = Number(total) || 0;
  if (t <= 0) return '—';
  return `${Math.round(((Number(parte) || 0) / t) * 100)}%`;
}

/** Idem, a partir de uma taxa já calculada (em %), com o denominador para o "—". */
export function porcentoDe(taxa, denominador) {
  if ((Number(denominador) || 0) <= 0) return '—';
  return `${Math.round(Number(taxa) || 0)}%`;
}

const dd = (n) => String(n).padStart(2, '0');

export const dataBr = (v) => {
  const d = paraData(v);
  return d ? `${dd(d.getDate())}/${dd(d.getMonth() + 1)}/${d.getFullYear()}` : '';
};

export const dataCurta = (v) => {
  const d = paraData(v);
  return d ? `${dd(d.getDate())}/${dd(d.getMonth() + 1)}` : '';
};

export const mesExtenso = (v) => {
  const d = paraData(v);
  return d ? `${MESES[d.getMonth()]}/${d.getFullYear()}` : '';
};

/**
 * O trecho de período do cabeçalho, pela regra do dono:
 *   dia     "11/09/2026"
 *   semana  "07 a 13/09"
 *   mes     "setembro/2026"
 */
export function rotuloPeriodo(periodo) {
  const { id, inicio, fim } = periodo ?? {};
  if (id === 'semana') return `${dd(paraData(inicio).getDate())} a ${dataCurta(fim)}`;
  if (id === 'mes') return mesExtenso(inicio);
  return dataBr(inicio);
}

/** "Resultado do Dia" / "da Semana" / "do Mês" — o cabeçalho do geral. */
export function tituloGeral(periodoId) {
  if (periodoId === 'semana') return 'Resultado da Semana';
  if (periodoId === 'mes') return 'Resultado do Mês';
  return 'Resultado do Dia';
}

/**
 * ★★ WO — O RESUMO DO DIA, EM DOIS FORMATOS. E a diferença é decisão do dono.
 *
 * ⚠⚠ O resumo é feito para ser colado no grupo do WhatsApp da clínica. Decisão do dono
 * (2/out): **o da SDR NÃO leva dinheiro.** Leads tentados, ligações, agendamentos e
 * comparecimentos — nada de R$. O do **negociador** leva fechamentos e valor.
 *
 * ★ A escolha é pela FUNÇÃO (`funcoes`), não pelo papel de acesso — é a separação que a WJ
 * estabeleceu: o JOABE tem `papel: 'sdr'` e `funcoes: ['negociador']`.
 *
 * ★ Há teste cobrando que o da SDR não contenha "R$".
 */
/**
 * ★★ "AAAA-MM-DD" → "DD/MM", SEM passar por `Date`. E isto é conserto de defeito medido.
 *
 * ⚠⚠ `dataCurta('2026-10-01')` devolvia **"30/09"**. `new Date('2026-10-01')` é meia-noite
 * **UTC** pela especificação da linguagem, e `getDate()` lê no fuso LOCAL — em São Paulo
 * (UTC−3) isso cai no dia anterior às 21h. ★ O resumo que a SDR colaria no grupo traria o
 * dia errado, e nada acusaria: o número estaria certo e só a data, errada.
 *
 * ★ O `dia` do painel JÁ é a data civil da clínica (veio de `diaDaData`, que formata no fuso
 * de São Paulo). Reconvertê-lo para `Date` só pode perder — então não se reconverte.
 */
export const diaCurto = (dia) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dia ?? ''));
  return m ? `${m[3]}/${m[2]}` : dataCurta(dia);
};

export function resumoDoDiaSdr(linha, dia) {
  if (!linha) return '';
  const l = [];
  l.push(`📋 ${diaCurto(dia)} — ${linha.nome || 'sem nome'}`);
  l.push(`Ligações: ${linha.tentativas} em ${linha.leadsTentados} leads (${linha.atendidas} atenderam)`);
  if (linha.reativacoes > 0) l.push(`Reativações: ${linha.reativacoes}`);
  const pendente = linha.agendouSemBaixa > 0 ? ` (${linha.agendouSemBaixa} sem baixa)` : '';
  l.push(`Agendou: ${linha.agendou}${pendente} · Compareceu: ${linha.compareceu}`);
  if (linha.proximosContatosCriados > 0) l.push(`Próximos contatos: ${linha.proximosContatosCriados}`);
  return l.join('\n');
}

export function resumoDoDiaNegociador(linha, dia) {
  if (!linha) return '';
  const l = [];
  l.push(`📋 ${diaCurto(dia)} — ${linha.nome || 'sem nome'}`);
  l.push(`Follow-ups: ${linha.followUps}`);
  l.push(`Fechou: ${linha.fechou} — ${moeda(linha.valor)}`);
  return l.join('\n');
}

/** ★ Qual dos dois: a FUNÇÃO decide. Negociador que também é SDR recebe o de negociador. */
export const resumoDoDia = (linha, dia, { ehNegociador = false } = {}) =>
  (ehNegociador ? resumoDoDiaNegociador : resumoDoDiaSdr)(linha, dia);
